import orderMetadataOptionTypes from './orderMetadataOptionTypes';
import { nullSafeToString } from '../lib/util';

/*
 * The API types preDefinedTextValues and adminDisplayOptions as strings, and what it actually returns
 * is JSON encoded inside those strings - `{"list":[{"id":"..","name":".."}]}`. They are parsed here so
 * consumers never see the double encoding. Malformed values degrade to an empty list rather than
 * throwing, since one bad metadata definition should not break a checkout.
 */

function parseList (value) {
    if (!value) {
        return [];
    }

    try {
        const parsed = typeof value === 'string' ? JSON.parse(value) : value;

        return Array.isArray(parsed.list) ? parsed.list : [];
    } catch (e) {
        console.warn(`Could not parse order metadata values: ${value}`);
        return [];
    }
}

/**
 * An order metadata field defined for this storefront, collected during checkout.
 *
 * @property {number} id - (Read only) The unique id for this option.
 * @property {string} label - (Read only) The user facing label. This is also the key the value is saved under.
 * @property {OrderMetadataOption~type} type - (Read only) The type of input required to collect this metadata.
 * @property {OrderMetadataOption~option[]} options - (Read only) If `this.type` is "select", the options to choose from.
 * @property {boolean} isRequired - (Read only) Whether this field is required.
 * @property {number} sequence - (Read only) The display order for this field.
 * @property {boolean} isCheckbox - (Read only) True if `this.type` is "checkbox".
 * @property {boolean} isText - (Read only) True if `this.type` is "text".
 * @property {boolean} isTextarea - (Read only) True if `this.type` is "textarea".
 * @property {boolean} isSelect - (Read only) True if `this.type` is "select".
 * @property {boolean} isDate - (Read only) True if `this.type` is "date".
 */

class OrderMetadataOption {
    constructor (data = {}) {
        this.id = data.id;
        this.label = nullSafeToString(data.name);
        this.type = orderMetadataOptionTypes[data.dataType && data.dataType.id] || null;
        this.options = Object.freeze(parseList(data.preDefinedTextValues)
            // Definitions in the wild contain blank filler entries; they are not selectable choices.
            .filter(option => nullSafeToString(option.name).trim())
            .map(option => Object.freeze({ label: option.name, value: option.id })));
        this.isRequired = Boolean(data.required);
        this.sequence = data.sequence;
        this.isCheckbox = this.type === orderMetadataOptionTypes.CB;
        this.isText = this.type === orderMetadataOptionTypes.EF;
        this.isTextarea = this.type === orderMetadataOptionTypes.TA;
        this.isSelect = this.type === orderMetadataOptionTypes.DD;
        this.isDate = this.type === orderMetadataOptionTypes.DP;
        /*
         * Kept verbatim so it can be echoed back on save. The project PATCH expects each metadata entry
         * as `{ [label]: { value, adminDisplayOptions } }`, and adminDisplayOptions is the definition's
         * own value round-tripped - it tells admin where to surface the answer, and dropping it would
         * silently change how the order prints.
         */
        this.adminDisplayOptions = data.adminDisplayOptions || null;

        Object.freeze(this);
    }
}

export default OrderMetadataOption;

/**
 * @typedef {"checkbox" | "text" | "textarea" | "select" | "date"} OrderMetadataOption~type
 */

/**
 * @typedef {Object} OrderMetadataOption~option
 * @property {string} label - The user facing label for this option.
 * @property {string} value - The value for this option.
 */
